import { useMemo, useState, useCallback, useEffect } from "react";
import { useAccount } from "wagmi";
import { useDispatch, useSelector } from "react-redux";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import useSanction from "@hooks/useSanction";
import { ETH, RSETH, WRSETH } from "@config/contractsConfig";
import { CONNECT_WALLET, YIELD_VAULT } from "@constants/analytics";
import {
  toFixedWithoutRounding,
  MAX_FRACTION_DIGITS,
  isNumber,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappConfig from "@hooks/useDappConfig";
import useDappChain from "@hooks/useDappChain";
import useTokenBalance from "@hooks/useTokenBalance";
import useExchangeRate from "@hooks/useExchangeRate";
import useYieldVaultAllowance from "@hooks/useYieldVaultAllowance";
import useYieldVaultXTokenAllowance from "@hooks/useYieldVaultXTokenAllowance";
import useHgEthExchangeRate from "@hooks/useHgEthExchangeRate";
import useEthConversion from "@hooks/useEthConversion";
import useDepositLimit from "@hooks/useDepositLimit";
import { updateUserData } from "store";
import { isFloat, toStringPrecision, trackEvent, emitReduxEvent } from "utils";
import { HIGH_GROWTH_VAULT_STRINGS } from "./constants";
import useYieldVaultData from "@hooks/useYieldVaultData";

export const useHgEthVault = () => {
  const {
    isLoadingAPYData,
    formattedApy,
    formattedTvl,
    isLoadingTotalStaked,
    formattedETHStaked,
  } = useYieldVaultData();

  const { isSanctioned } = useSanction();

  const vaultHeaderData = useMemo(
    () => ({
      isLoadingAPYData,
      isLoadingTotalStaked,
      totalStaked:
        formattedTvl && formattedTvl !== "-" ? `$${formattedTvl}` : "-",
      totalStakedEth: formattedETHStaked,
      apyValue: formattedApy,
      apyLabel: HIGH_GROWTH_VAULT_STRINGS.HEADER.STATS.APY,
      totalStakedLabel: HIGH_GROWTH_VAULT_STRINGS.HEADER.STATS.TVL,
      title: HIGH_GROWTH_VAULT_STRINGS.HEADER.TITLE,
      logo: {
        src: "/assets/vaults/hgeth.svg",
        label: "hgETH Logo",
      },
      auditedUrl:
        "https://kerneldao.gitbook.io/kernel/getting-started/gain/high-growth-gain#audits",
    }),
    [
      isLoadingAPYData,
      isLoadingTotalStaked,
      formattedTvl,
      formattedETHStaked,
      formattedApy,
    ]
  );

  const tabsData = useMemo(
    () => HIGH_GROWTH_VAULT_STRINGS.SECTIONS.DEPOSIT_WITHDRAW.TABS,
    []
  );

  const portfolioConfig = useMemo(
    () => ({
      tabs: HIGH_GROWTH_VAULT_STRINGS.SECTIONS.PORTFOLIO.TABS,
      points: HIGH_GROWTH_VAULT_STRINGS.SECTIONS.PORTFOLIO.POINTS,
      overviewData: HIGH_GROWTH_VAULT_STRINGS.SECTIONS.OVERVIEW.DATA,
      aprReports: HIGH_GROWTH_VAULT_STRINGS.SECTIONS.PORTFOLIO.APR_REPORTS,
    }),
    []
  );

  return {
    vaultHeaderData,
    tabsData,
    portfolioConfig,
    isSanctioned,
    constants: HIGH_GROWTH_VAULT_STRINGS,
  };
};

// High Growth Vault specific deposit logic
export const useHgEthDeposit = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();
  const { isYieldVaultSupportedChain, config, yieldVaultConfig } =
    useDappConfig();
  const { erc20Allowance, minDepositAmount } = useYieldVaultAllowance();
  const {
    tokensBalances,
    xTokenTokenBalance,
    xTokenYieldVaultBalance,
    nativeTokenBalance,
  } = useTokenBalance();

  const { selectedERC20YieldVault, referralId } = useSelector(
    (state: any) => state.user
  );
  const dispatch = useDispatch();
  const { openConnectModal } = useConnectModal();

  const tokenBalance = useMemo(() => {
    if (selectedERC20YieldVault === config.xtoken.symbol)
      return xTokenTokenBalance;
    else if (selectedERC20YieldVault === ETH) return nativeTokenBalance;
    return tokensBalances[selectedERC20YieldVault];
  }, [
    xTokenTokenBalance,
    tokensBalances,
    selectedERC20YieldVault,
    config.xtoken.symbol,
    nativeTokenBalance,
  ]);

  const conversionsDatahgEth = useHgEthExchangeRate();
  const conversionDataRsEth = useExchangeRate("yieldvault");

  const conversionsRsETH = useMemo(() => {
    const conversionToERC20 = conversionDataRsEth?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionDataRsEth?.exchangeRate]);

  const gethgEthExchange = useCallback(() => {
    const rsETHRate =
      selectedERC20YieldVault === RSETH || selectedERC20YieldVault === WRSETH
        ? 1
        : Number(conversionsRsETH?.toERC20);
    return Number(conversionsDatahgEth?.toERC20) * rsETHRate;
  }, [
    conversionsDatahgEth?.toERC20,
    conversionsRsETH?.toERC20,
    selectedERC20YieldVault,
  ]);

  const { valueInEth } = useEthConversion(conversionsDatahgEth?.toERC20);

  const { depositLimit, maxDeposit } = useDepositLimit(
    config.xtoken.address,
    conversionsRsETH
  );

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't deposit more than available balance in your wallet.`;
      } else if (Number(value) < minDepositAmount) {
        error = `hgETH vault has a minimum deposit of ${minDepositAmount} ${selectedERC20YieldVault}.
            Please try with a higher amount.`;
      } else if (Number(value) > Number(depositLimit)) {
        error = `hgETH vault allows up to ${maxDeposit} ${selectedERC20YieldVault}.
            You can deposit up to ${toFixedWithoutRounding(
              Number(depositLimit).toString(),
              4
            )} ${selectedERC20YieldVault}`;
      }
      return error;
    },
    [
      isConnected,
      tokenBalance,
      selectedERC20YieldVault,
      amount,
      minDepositAmount,
      maxDeposit,
      depositLimit,
    ]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  useEffect(() => {
    setError("");
  }, [selectedERC20YieldVault]);

  const isApproved = useMemo(() => {
    if (selectedERC20YieldVault === ETH) return true;
    return (
      erc20Allowance &&
      Number(erc20Allowance) &&
      Number(erc20Allowance) >= Number(amount)
    );
  }, [erc20Allowance, amount, selectedERC20YieldVault]);

  const convertedAmount = useMemo(() => {
    const hgRate = gethgEthExchange();
    if (amount && !isNaN(Number(amount))) {
      const value = Number(amount) / (hgRate > 0 ? hgRate : 1);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, gethgEthExchange]);

  const handleClick = useCallback(
    (inputAmount?: string) => {
      const amountToUse = inputAmount || amount;

      if (!isConnected) {
        openConnectModal?.();
        trackEvent(CONNECT_WALLET, {
          type: YIELD_VAULT.DEPOSIT_CTA,
        });
      } else if (!isYieldVaultSupportedChain) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else {
        if (isApproved) {
          if (!amountToUse) {
            setError(
              "This field can't be blank or zero. Please provide valid input."
            );
            return;
          }

          emitReduxEvent(EVENTS.HANDLE_YIELD_VAULT_DEPOSIT, {
            amount: amountToUse,
            referralId,
          });

          setAmount("");
        } else {
          emitReduxEvent(EVENTS.HANDLE_YIELD_VAULT_APPROVE, {
            amount: amountToUse,
          });
        }
      }
    },
    [
      isConnected,
      isYieldVaultSupportedChain,
      isApproved,
      amount,
      openConnectModal,
      referralId,
    ]
  );

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    const maxAmount = Math.min(tokenBalance, maxDeposit);
    handleChange(toFixedWithoutRounding(maxAmount.toString(), 4));
  }, [tokenBalance, maxDeposit]);

  const handleTokenSelect = (key: string) => {
    dispatch(
      updateUserData({
        ["selectedERC20YieldVault"]: key,
      })
    );
  };

  return {
    amount,
    setAmount,
    error,
    setError,
    checkInput,
    tokenBalance,
    isConnected,
    isApproved,
    convertedAmount,
    exchangeRate: gethgEthExchange(),
    selectedERC20Vault: selectedERC20YieldVault,
    vaultConfig: yieldVaultConfig,
    xTokenVaultBalance: xTokenYieldVaultBalance,
    conversionsDataAgEth: conversionsDatahgEth,
    valueInEth,
    handleClick,
    handleChange,
    handleMaxClick,
    handleTokenSelect,
    isYieldVaultSupportedChain,
  };
};

// High Growth Vault specific withdraw logic
export const useHgEthWithdraw = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();
  const { isEthereumChain } = useDappChain();
  const { yieldVaultConfig, isYieldVaultSupportedChain } = useDappConfig();
  const { xTokenTokenBalance, xTokenYieldVaultBalance } = useTokenBalance();
  const { xTokenAllowance } = useYieldVaultXTokenAllowance();
  const { openConnectModal } = useConnectModal();

  const tokenBalance = xTokenYieldVaultBalance;

  const isApproved = useMemo(() => {
    return (
      xTokenAllowance &&
      Number(xTokenAllowance) &&
      Number(xTokenAllowance) >= Number(amount)
    );
  }, [xTokenAllowance, amount]);

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't withdraw more than available balance in your wallet.`;
      }
      return error;
    },
    [isConnected, tokenBalance, amount]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  const conversionsDataHgEth = useHgEthExchangeRate();
  const { valueInEth } = useEthConversion(conversionsDataHgEth?.toERC20);

  const convertedAmount = useMemo(() => {
    if (amount && isNumber(amount)) {
      const value = Number(amount) * Number(conversionsDataHgEth?.toERC20);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, conversionsDataHgEth]);

  const handleClick = useCallback(
    (inputAmount?: string) => {
      const amountToUse = inputAmount || amount;

      if (!isConnected) {
        openConnectModal?.();
        trackEvent(CONNECT_WALLET, {
          type: YIELD_VAULT.WITHDRAW_CTA,
        });
      } else if (!isEthereumChain) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else {
        if (isApproved) {
          if (!amountToUse) {
            setError(
              "This field can't be blank or zero. Please provide valid input."
            );
            return;
          }
          emitReduxEvent(EVENTS.HANDLE_YIELD_VAULT_WITHDRAW, {
            amount: amountToUse,
          });
          setAmount("");
        } else {
          emitReduxEvent(EVENTS.HANDLE_YIELD_VAULT_XTOKEN_APPROVE, {
            amount: amountToUse,
          });
        }
      }
    },
    [isConnected, amount, openConnectModal, isApproved, isEthereumChain]
  );

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    handleChange(toFixedWithoutRounding(tokenBalance.toString(), 4));
  }, [tokenBalance]);

  return {
    amount,
    setAmount,
    error,
    setError,
    checkInput,
    tokenBalance,
    isConnected,
    isApproved,
    convertedAmount,
    exchangeRate: Number(conversionsDataHgEth?.toERC20),
    vaultConfig: yieldVaultConfig,
    xTokenTokenBalance,
    conversionsDataAgEth: conversionsDataHgEth,
    valueInEth,
    handleClick,
    handleChange,
    handleMaxClick,
    isYieldVaultSupportedChain,
  };
};
